/**
 * readGate — whether a block may write yet.
 *
 * A GROUP CANNOT AMEND A ROW IT CANNOT NAME. Every inline group seeds its draft from a read, and on
 * this canvas those reads answer on their own clocks — the registration's is keyed to the vehicle,
 * the roadworthiness record's to the contract, and neither is the read that put the area on screen.
 * Until one of them answers, its block is drawing fields that take typing, seeded with empties that
 * mean "not here yet" rather than "empty", over a row id that is still `null`. A commit taken in
 * that window does not amend the record at all: it writes the block's blanks as an INSERT and
 * points the owner at the new row, leaving the plate, the certificate and the payment already on
 * file behind — recorded, and visible to nobody.
 *
 * RULE 6 DOES NOT COVER THIS ONE, WHICH IS WHY IT IS A GATE RATHER THAN A STATEMENT. `touched`
 * separates a stale empty from a deliberate clearing, so a write can stay silent about the fields
 * nobody typed into. It has nothing to say about the row id, which is not a field and was never in
 * the draft — whatever such a write states, it states it about the wrong row. While the read is out
 * the only safe write is none.
 *
 * `undefined` IS NOT AN ANSWER; `null` IS. A car with no registration and a contract with no
 * certificate both read `null`, and those are exactly the states in which a block CREATES its row —
 * closing on them would make the first save impossible. Reading the ANSWER rather than the request
 * is also what keeps a background refetch open: the record is still there, and reducer rule 1
 * already protects the draft from the values landing underneath it.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

/**
 * True while the read that names a block's record has produced no answer — the state its writes
 * must fail closed in.
 *
 * IT COVERS A FAILED READ TOO, and deliberately: a read still in flight and a read that came back
 * refused leave the block in the same position, unable to say which row it is describing.
 *
 * IT TAKES THE RECORD, NOT A FLAG. `object | null | undefined` rather than `unknown` so that
 * handing it the query's `isLoading` — the near-miss this whole gate is about — is a type error
 * rather than a gate that reads the request again.
 */
export function readUnanswered(record: object | null | undefined): boolean {
    return record === undefined;
}
